import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import type { AuthContextType, UserRole, AllowedUser } from '@/types/auth'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const SAFETY_TIMEOUT_MS = 8000

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)
  const [userRole, setUserRole] = useState<UserRole | null>(null)

  const mountedRef = useRef(true)

  /** Sprawdź czy email jest w tabeli seo_allowed_users */
  const checkAllowedUser = async (email: string): Promise<AllowedUser | null> => {
    const { data, error } = await supabase
      .from('seo_allowed_users')
      .select('*')
      .eq('email', email)
      .maybeSingle()

    if (error) {
      console.error('Błąd sprawdzania uprawnień:', error)
      return null
    }

    return data as AllowedUser | null
  }

  /** Ustaw stan po sprawdzeniu allowed_users */
  const processUserAccess = async (currentUser: User | null) => {
    if (!currentUser?.email) {
      setHasAccess(false)
      setAccessDenied(false)
      setUserRole(null)
      return
    }

    const allowedUser = await checkAllowedUser(currentUser.email)

    if (!mountedRef.current) return

    if (allowedUser) {
      setHasAccess(true)
      setAccessDenied(false)
      setUserRole(allowedUser.role)
    } else {
      setHasAccess(false)
      setAccessDenied(true)
      setUserRole(null)
    }
  }

  /** Ponowne sprawdzenie uprawnień */
  const refreshPermissions = async () => {
    if (user) {
      await processUserAccess(user)
    }
  }

  /** Wylogowanie */
  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setSession(null)
    setHasAccess(false)
    setAccessDenied(false)
    setUserRole(null)
    setIsPasswordRecovery(false)
  }

  useEffect(() => {
    mountedRef.current = true

    // Safety timeout — jeśli ładowanie trwa za długo, wyłącz spinner
    const safetyTimer = setTimeout(() => {
      if (mountedRef.current && loading) {
        console.warn('Auth safety timeout — wymuszam koniec ładowania')
        setLoading(false)
      }
    }, SAFETY_TIMEOUT_MS)

    // 1. Odśwież sesję (getUser wymusza walidację + refresh tokenu)
    const initSession = async () => {
      try {
        const { data: { user: freshUser }, error } = await supabase.auth.getUser()
        if (!mountedRef.current) return

        if (error || !freshUser) {
          setSession(null)
          setUser(null)
          setLoading(false)
          return
        }

        // Po getUser() sesja jest odświeżona — teraz getSession() zwraca aktualny token
        const { data: { session: freshSession } } = await supabase.auth.getSession()
        if (!mountedRef.current) return

        setSession(freshSession)
        setUser(freshUser)

        await processUserAccess(freshUser)
      } catch {
        // Fallback na brak sesji
        setSession(null)
        setUser(null)
      } finally {
        if (mountedRef.current) {
          setLoading(false)
        }
      }
    }

    initSession()

    // 2. Nasłuchuj zmian sesji
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Pitfall fix: setTimeout aby uniknąć race condition
        setTimeout(() => {
          if (!mountedRef.current) return

          setSession(newSession)
          setUser(newSession?.user ?? null)

          if (event === 'PASSWORD_RECOVERY') {
            setIsPasswordRecovery(true)
          }

          if (event === 'SIGNED_IN' && newSession?.user) {
            processUserAccess(newSession.user).then(() => {
              if (mountedRef.current) {
                setLoading(false)
              }
            })
          }

          if (event === 'SIGNED_OUT') {
            setHasAccess(false)
            setAccessDenied(false)
            setUserRole(null)
            setIsPasswordRecovery(false)
            setLoading(false)
          }
        }, 0)
      }
    )

    return () => {
      mountedRef.current = false
      clearTimeout(safetyTimer)
      subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value: AuthContextType = {
    user,
    session,
    loading,
    isAuthenticated: !!session && !!user,
    hasAccess,
    accessDenied,
    isPasswordRecovery,
    userRole,
    isAdmin: userRole === 'admin',
    signOut,
    refreshPermissions,
  }

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth musi być użyty wewnątrz AuthProvider')
  }
  return context
}
